/**
 * Cliente da API de disparos por SMS + formatacao de valores.
 *
 * REGRA: o backend nunca manda float. Dinheiro chega em CENTAVOS inteiros,
 * preco unitario em MILESIMOS, percentual em DECIMOS de ponto e ROI em
 * CENTESIMOS. A formatacao acontece aqui, na borda de exibicao — nenhuma conta
 * de dinheiro e' feita em ponto flutuante no JavaScript, onde
 * 0.1 + 0.2 === 0.30000000000000004.
 */
import axios from "axios";

const API = "/api";

const auth = () => {
  const token = localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

/** 1792 -> "R$ 17,92". Divisao inteira, sem float. */
export const reais = (centavos) => {
  const v = Math.trunc(Number(centavos) || 0);
  const sinal = v < 0 ? "-" : "";
  const abs = Math.abs(v);
  const inteiro = Math.trunc(abs / 100);
  const resto = abs % 100;
  return `${sinal}R$ ${inteiro.toLocaleString("pt-BR")},${String(resto).padStart(2, "0")}`;
};

/** 961 -> "96,1%" */
export const pct = (decimos) =>
  decimos === null || decimos === undefined
    ? "—"
    : `${Math.trunc(decimos / 10)},${decimos % 10}%`;

/** "5534999658914" -> "+55(34)9.9965-8914" (formato pedido pelo dono). */
export const fone = (msisdn) => {
  const d = String(msisdn || "").replace(/\D/g, "");
  if (d.length !== 13 || !d.startsWith("55")) return msisdn || "";
  return `+55(${d.slice(2, 4)})${d[4]}.${d.slice(5, 9)}-${d.slice(9)}`;
};

/** "2026-08-27T04:52:xx" -> "27/08/2026 4:52" (formato pedido pelo dono). */
export const dataBr = (iso) => {
  if (!iso || iso.length < 16) return "";
  const hora = parseInt(iso.slice(11, 13), 10);
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)} ${hora}:${iso.slice(14, 16)}`;
};

/** Agendamento ("2026-08-27 14:15:00") ainda esta no futuro? */
export const agendadaNoFuturo = (ag) =>
  !!ag && new Date(ag.replace(" ", "T")) > new Date();

/** Percentual sem casa decimal quando redonda: "100%" em vez de "100,0%". */
export const pctCurto = (decimos) => pct(decimos).replace(",0%", "%");

/** 3571 -> "35,7x" */
export const roi = (centesimos) =>
  centesimos === null || centesimos === undefined
    ? "—"
    : `${Math.trunc(centesimos / 100)},${String(Math.trunc((centesimos % 100) / 10))}x`;

export const smsApi = {
  dashboard: (dias = 30, canal = "sms") => axios.get(`${API}/sms/dashboard?dias=${dias}&canal=${canal}`, auth()).then((r) => r.data),
  carteira: () => axios.get(`${API}/sms/carteira`, auth()).then((r) => r.data),
  criarRecarga: (valorCentavos) =>
    axios.post(`${API}/sms/carteira/recarga`, { valor_centavos: valorCentavos }, auth()).then((r) => r.data),
  statusRecarga: (paymentId) =>
    axios.get(`${API}/sms/carteira/recarga/status?payment_id=${paymentId}`, auth()).then((r) => r.data),
  publico: () => axios.get(`${API}/sms/publico`, auth()).then((r) => r.data),
  segmentos: () => axios.get(`${API}/sms/segmentos`, auth()).then((r) => r.data),
  campanhas: () => axios.get(`${API}/sms/campanhas`, auth()).then((r) => r.data),
  orcar: (body) => axios.post(`${API}/sms/campanhas/orcamento`, body, auth()).then((r) => r.data),
  criar: (body) => axios.post(`${API}/sms/campanhas`, body, auth()).then((r) => r.data),
  enviarTeste: (body) => axios.post(`${API}/sms/campanhas/teste`, body, auth()).then((r) => r.data),
  disparar: (id) => axios.post(`${API}/sms/campanhas/${id}/disparar`, {}, auth()).then((r) => r.data),
  termos: (canal = "sms") => axios.get(`${API}/sms/termos?canal=${canal}`, auth()).then((r) => r.data),
  aceitarTermos: (canal = "sms") => axios.post(`${API}/sms/termos/aceitar?canal=${canal}`, {}, auth()).then((r) => r.data),
  subirAudioVoz: (arquivo) => {
    const fd = new FormData();
    fd.append("arquivo", arquivo);
    return axios.post(`${API}/sms/voz/audio`, fd, auth()).then((r) => r.data);
  },
  cancelar: (id) => axios.post(`${API}/sms/campanhas/${id}/cancelar`, {}, auth()).then((r) => r.data),
  relatorio: (id) => axios.get(`${API}/sms/campanhas/${id}`, auth()).then((r) => r.data),
  destinatarios: (id) => axios.get(`${API}/sms/campanhas/${id}/destinatarios`, auth()).then((r) => r.data),
};

/** CSV com BOM: sem ele o Excel abre acento quebrado e o lojista reclama. */
export function baixarCsv(nome, linhas) {
  if (!linhas?.length) return;
  const colunas = Object.keys(linhas[0]);
  const escapar = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [colunas.join(";"), ...linhas.map((l) => colunas.map((c) => escapar(l[c])).join(";"))].join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${nome}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}
